/**
 * Servicio para gestión de citas a través de la nueva API
 * Centraliza todas las operaciones de citas con la API backend
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';
import { citaTransforms } from '../utils/dataTransforms';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';

/**
 * Obtiene todas las citas
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @param {number} clienteId - ID del cliente (opcional)
 * @returns {Promise<Array>} - Lista de citas
 */
export const getCitas = async (sucursalId = null, clienteId = null) => {
  try {
    //console.log('📅 [CitasService] Obteniendo datos');
    
    const params = {};
    if (sucursalId) params.sucursal_id = sucursalId;
    if (clienteId) params.cliente_id = clienteId;
    
    const response = await apiHelpers.citas.get(API_ENDPOINTS.CITAS.BASE, { params });
    
    if (response.data) {
      const citasData = response.data.data || response.data.citas || response.data;
      const transformedData = citaTransforms.fromApiList(citasData);
      return transformedData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [CitasService] Error al obtener citas:', error);
    console.error('❌ [CitasService] Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url
    });
    throw error;
  }
};

/**
 * Obtiene citas de un cliente específico
 * @param {Object} user - Usuario actual (puede contener cliente info)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} - Lista de citas del cliente
 */
export const getCitasCliente = async (user, sucursalId = null) => {
  try {
    //console.log('📅 [CitasService] Obteniendo datos');
    
    // Obtener cliente_id del usuario
    let clienteId = null;
    if (user?.cliente_id) {
      clienteId = user.cliente_id;
    } else if (user?.id) {
      // Si el usuario es un cliente, usar su ID
      clienteId = user.id;
    }
    
    if (!clienteId) {
      console.warn('⚠️ [CitasService] No se pudo determinar cliente_id del usuario');
      return [];
    }
    
    const response = await apiHelpers.citas.get(API_ENDPOINTS.CITAS.BY_CLIENTE(clienteId), {
      params: sucursalId ? { sucursal_id: sucursalId } : {}
    });
    
    if (response.data) {
      const citasData = response.data.data || response.data.citas || response.data;
      const transformedData = citaTransforms.fromApiList(citasData);
      return transformedData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [CitasService] Error al obtener citas del cliente:', error);
    throw error;
  }
};

/**
 * Obtiene citas por empresa
 * @param {number} empresaId - ID de la empresa
 * @returns {Promise<Array>} - Lista de citas de la empresa
 */
export const getCitasByEmpresa = async (empresaId) => {
  try {
    //console.log('📅 [CitasService] Obteniendo datos');
    
    // Primero obtener todas las sucursales de la empresa
    const sucursalesResponse = await apiHelpers.citas.get(API_ENDPOINTS.SUCURSALES.BASE, {
      params: { empresa_id: empresaId }
    });
    
    if (!sucursalesResponse.data || !sucursalesResponse.data.data) {
      return [];
    }
    
    const sucursales = sucursalesResponse.data.data;
    
    if (sucursales.length === 0) {
      return [];
    }
    
    // Obtener citas para cada sucursal y combinar resultados
    const todasLasCitas = [];
    
    for (const sucursal of sucursales) {
      try {
        const citasSucursal = await getCitas(sucursal.id);
        
        if (citasSucursal && citasSucursal.length > 0) {
          todasLasCitas.push(...citasSucursal);
        }
      } catch (error) {
        console.error(`❌ [CitasService] ERROR en getCitas para sucursal ${sucursal.id}:`, error);
        // Continuar con otras sucursales
      }
    }
    
    // Ordenar por fecha y hora (más recientes primero)
    todasLasCitas.sort((a, b) => {
      const fechaA = new Date(`${a.Fecha} ${a.Hora}`);
      const fechaB = new Date(`${b.Fecha} ${b.Hora}`);
      return fechaB - fechaA;
    });
    
    return todasLasCitas;
  } catch (error) {
    console.error('❌ [CitasService] ERROR GENERAL en getCitasByEmpresa:', error);
    throw error;
  }
};

/**
 * Crea una nueva cita
 * @param {Object} citaData - Datos de la cita
 * @param {Object} user - Usuario actual (opcional)
 * @returns {Promise<Object>} - Cita creada
 */
export const createCita = async (citaData, user = null) => {
  try {
    //console.log('📅 [CitasService] Creando registro');
    //console.log('📝 [CitasService] Creando nueva cita...', citaData);
    
    const transformedData = citaTransforms.toApi(citaData);
    
    // Si hay usuario y no hay cliente_id, intentar usar el usuario como cliente
    if (user && !transformedData.cliente_id) {
      if (user.cliente_id) {
        transformedData.cliente_id = user.cliente_id;
      } else if (user.id) {
        transformedData.cliente_id = user.id;
      }
    }
    
    const response = await apiHelpers.citas.post(API_ENDPOINTS.CITAS.BASE, transformedData);
    
    if (response.data) {
      const citaData = response.data.data || response.data.cita || response.data;
      const transformedResponse = citaTransforms.fromApi(citaData);
      //console.log('✅ [CitasService] Cita creada:', transformedResponse);
      return transformedResponse;
    } else {
      throw new Error('No se recibió respuesta válida del servidor');
    }
  } catch (error) {
    console.error('❌ [CitasService] Error al crear cita:', error);
    throw error;
  }
};

/**
 * Actualiza una cita
 * @param {number} id - ID de la cita
 * @param {Object} citaData - Datos actualizados de la cita
 * @returns {Promise<Object>} - Cita actualizada
 */
export const updateCita = async (id, citaData) => {
  try {
    //console.log('📅 [CitasService] Actualizando registro');
    //console.log('📝 [CitasService] Actualizando cita...', { id, citaData });
    
    const transformedData = citaTransforms.toApi(citaData);
    
    const response = await apiHelpers.citas.put(API_ENDPOINTS.CITAS.BY_ID(id), transformedData);
    
    if (response.data) {
      const citaData = response.data.data || response.data.cita || response.data;
      const transformedResponse = citaTransforms.fromApi(citaData);
      //console.log('✅ [CitasService] Cita actualizada:', transformedResponse);
      return transformedResponse;
    } else {
      throw new Error('No se recibió respuesta válida del servidor');
    }
  } catch (error) {
    console.error('❌ [CitasService] Error al actualizar cita:', error);
    throw error;
  }
};

/**
 * Elimina una cita
 * @param {number} id - ID de la cita
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export const deleteCita = async (id) => {
  try {
    //console.log('📅 [CitasService] Eliminando registro');
    //console.log('🗑️ [CitasService] Eliminando cita...', { id });
    
    const response = await apiHelpers.citas.delete(API_ENDPOINTS.CITAS.BY_ID(id));
    
    //console.log('✅ [CitasService] Cita eliminada');
    return true;
  } catch (error) {
    console.error('❌ [CitasService] Error al eliminar cita:', error);
    throw error;
  }
};

/**
 * Guarda servicios seleccionados para una cita
 * @param {number} citaId - ID de la cita
 * @param {Array} servicios - Lista de servicios seleccionados
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const guardarServiciosSeleccionados = async (citaId, servicios) => {
  try {
    //console.log('📅 [CitasService] Procesando operación');
    //console.log('💼 [CitasService] Guardando servicios seleccionados...', { citaId, servicios });
    
    const response = await apiHelpers.citas.post(API_ENDPOINTS.CITAS.SERVICIOS(citaId), {
      servicios: servicios
    });
    
    if (response.data) {
      //console.log('✅ [CitasService] Servicios guardados');
      return response.data;
    } else {
      throw new Error('No se recibió respuesta válida del servidor');
    }
  } catch (error) {
    console.error('❌ [CitasService] Error al guardar servicios:', error);
    throw error;
  }
};

/**
 * Obtiene servicios seleccionados de una cita
 * @param {number} citaId - ID de la cita
 * @returns {Promise<Array>} - Lista de servicios seleccionados
 */
export const getServiciosSeleccionados = async (citaId) => {
  try {
    //console.log('📅 [CitasService] Obteniendo datos');
    const response = await apiHelpers.citas.get(API_ENDPOINTS.CITAS.SERVICIOS(citaId));
    
    if (response.data) {
      const serviciosData = response.data.data || response.data.servicios || response.data;
      return serviciosData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [CitasService] Error al obtener servicios seleccionados:', error);
    throw error;
  }
};

/**
 * Elimina servicios seleccionados de una cita
 * @param {number} citaId - ID de la cita
 * @returns {Promise<boolean>} - true si se eliminaron correctamente
 */
export const eliminarServiciosSeleccionados = async (citaId) => {
  try {
    //console.log('📅 [CitasService] Eliminando registro');
    //console.log('🗑️ [CitasService] Eliminando servicios seleccionados...', { citaId });
    
    const response = await apiHelpers.citas.delete(API_ENDPOINTS.CITAS.SERVICIOS(citaId));
    
    //console.log('✅ [CitasService] Servicios eliminados');
    return true;
  } catch (error) {
    console.error('❌ [CitasService] Error al eliminar servicios:', error);
    throw error;
  }
};

/**
 * Busca citas con filtros
 * @param {Object} filtros - Filtros de búsqueda
 * @returns {Promise<Array>} - Lista de citas que coinciden con los filtros
 */
export const buscarCitas = async (filtros = {}) => {
  try {
    //console.log('📅 [CitasService] Procesando operación');
    const response = await apiHelpers.citas.get(API_ENDPOINTS.CITAS.BUSCAR, {
      params: filtros
    });
    
    if (response.data) {
      const citasData = response.data.data || response.data.citas || response.data;
      const transformedData = citaTransforms.fromApiList(citasData);
      return transformedData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [CitasService] Error al buscar citas:', error);
    throw error;
  }
};